/**
 * The market, drawn over the price.
 *
 * A candlestick chart of BTC is a chart of BTC. What makes it a chart of *this
 * product* is the minute grid laid over it: where each market's sixty seconds
 * begin and end. `lightweight-charts` has no vertical-line API — it is a chart
 * of a price series, and the time axis belongs to the series — so this is drawn
 * as a series primitive, which is the extension point the library provides for
 * exactly this.
 *
 * One mark, and it answers a question a trader actually asks: **the expiry
 * boundary**, 1px `border-strong`, at every `expiry_at_ms`, in `accent-solid`
 * for the live market. "When does this minute end?" On a 60-second product that
 * is the single most important thing the time axis can say, and a bare clock
 * axis does not say it.
 *
 * **There were two shaded bands here and both are gone.** A `bg-active` fill
 * over `[expiry − W, expiry)` drew the settlement window, and a half-alpha fill
 * over `[anchor, expiry)` tinted the live minute. The argument for them was that
 * a visitor should see the window they will be settled against approaching — but
 * the canvas is transparent so that the countdown underlay reads *through* it
 * (§2.10 Geometry), and anything this primitive fills is therefore painted over
 * the digits. A ten-second block landing squarely on the only clock on the page
 * costs more than the shading explains, and on the `market` viewport the live
 * tint is the whole pane. The ten seconds that decide a market are named in
 * words by `<ResolutionPanel>` and `<StrikeMeter>`, which is where a sentence
 * belongs.
 *
 * Strike lines are *not* here either: `series.createPriceLine` already draws a
 * horizontal line with an axis label, and re-implementing that on a canvas would
 * be worse in every respect.
 *
 * No React and no DOM beyond the canvas the library hands it.
 */

import type {
  IPrimitivePaneRenderer,
  IPrimitivePaneView,
  ISeriesPrimitive,
  PrimitivePaneViewZOrder,
  SeriesAttachedParameter,
  Time,
} from 'lightweight-charts'

/**
 * One market's footprint on the time axis.
 *
 * It is the expiry and the strike, and no longer the anchor: `expiry(N)` *is*
 * `anchor(N+1)` (§F26), so on a continuous series every anchor is already drawn
 * as its predecessor's boundary, and a field nothing reads is a field the next
 * reader has to rule out.
 */
export interface MarketBand {
  marketId: string
  /** `expiry_at_ms` — where trading stops *and* the settlement window closes,
   *  since §F26. */
  expiryAtMs: number
  /** The `strike` observation, or `null` while `SCHEDULED`/`FIXING` — and,
   *  since §F73, for the first second or two of `OPEN`, while the print stamped
   *  at the anchor is on its way. A band with no strike draws no price line. */
  strike: unknown
}

interface BandTokens {
  borderStrong: string
  accentSolid: string
}

/**
 * The subset of the chart the renderer needs.
 *
 * Held rather than passed because `draw` is called by the library on its own
 * schedule — a pan, a zoom, a new bar — and has to be able to convert a
 * timestamp to a pixel at that moment rather than at the moment the bands were
 * set.
 */
interface Converter {
  timeToX(timeSeconds: number): number | null
}

export class MarketBandsPrimitive implements ISeriesPrimitive<Time> {
  #bands: readonly MarketBand[] = []
  #currentMarketId: string | null = null
  #converter: Converter | null = null
  #requestUpdate: (() => void) | null = null
  readonly #tokens: () => BandTokens
  readonly #view: IPrimitivePaneView

  constructor(tokens: () => BandTokens) {
    this.#tokens = tokens
    this.#view = {
      // Behind the candles. The boundary is context and must never sit on top
      // of the data it is context for.
      zOrder: (): PrimitivePaneViewZOrder => 'bottom',
      renderer: (): IPrimitivePaneRenderer => ({
        draw: (target) => this.#draw(target),
      }),
    }
  }

  attached(param: SeriesAttachedParameter<Time>): void {
    this.#requestUpdate = param.requestUpdate
    this.#converter = {
      timeToX: (timeSeconds) => param.chart.timeScale().timeToCoordinate(timeSeconds as Time),
    }
  }

  detached(): void {
    this.#converter = null
    this.#requestUpdate = null
  }

  paneViews(): readonly IPrimitivePaneView[] {
    return [this.#view]
  }

  setBands(bands: readonly MarketBand[], currentMarketId: string | null): void {
    this.#bands = bands
    this.#currentMarketId = currentMarketId
    this.#requestUpdate?.()
  }

  // -------------------------------------------------------------------------

  #draw(target: {
    useBitmapCoordinateSpace: (
      fn: (scope: {
        context: CanvasRenderingContext2D
        bitmapSize: { width: number; height: number }
        horizontalPixelRatio: number
        verticalPixelRatio: number
      }) => void,
    ) => void
  }): void {
    const converter = this.#converter
    if (!converter || this.#bands.length === 0) return
    const tokens = this.#tokens()

    target.useBitmapCoordinateSpace(({ context: ctx, bitmapSize, horizontalPixelRatio }) => {
      ctx.save()
      for (const band of this.#bands) {
        const expiryX = converter.timeToX(band.expiryAtMs / 1000)
        if (expiryX === null) continue
        const live = band.marketId === this.#currentMarketId

        ctx.strokeStyle = live ? tokens.accentSolid : tokens.borderStrong
        ctx.lineWidth = Math.max(1, Math.floor(horizontalPixelRatio))
        ctx.beginPath()
        const x = Math.round(expiryX * horizontalPixelRatio) + 0.5
        ctx.moveTo(x, 0)
        ctx.lineTo(x, bitmapSize.height)
        ctx.stroke()
      }
      ctx.restore()
    })
  }
}
